import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    Component,
    Inject,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from "@angular/core";
import { GoogleMap, GoogleMapsModule } from "@angular/google-maps";
import { EMPTY, of, ReplaySubject, Subscription } from "rxjs";
import {
    debounceTime,
    filter,
    map,
    pairwise,
    startWith,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { Coordinates } from "@hermes/api-model-common";
import { Context, LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { LoadGeoLibrariesService, MapBounds } from "@hermes/map-utils";
import { ExternalLibraryFacade } from "@hermes/states/external-library";
import { calculateDistance } from "@hermes/utils-generic/helpers";

import { AbstractMapComponent } from "../../abstract-base-map.component";
import { MarkerData } from "../../map.type";
import { MapGoogleMarkerComponent } from "../map-google-marker/map-google-marker.component";

export const DEFAULT_ZOOM_LEVEL = 12;
export const DEFAULT_INCREMENT_ZOOM_LEVEL = 2;

@Component({
    selector: "h-map-google-display",
    templateUrl: "./map-google-display.component.html",
    styleUrls: ["./map-google-display.component.scss"],
    imports: [AsyncPipe, GoogleMapsModule, MapGoogleMarkerComponent],
})
export class MapGoogleDisplayComponent
    extends AbstractMapComponent
    implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
    @ViewChildren("map")
    private googleMapElement!: QueryList<GoogleMap>;

    public googleMapComponent?: GoogleMap;

    public options: google.maps.MapOptions = {
        zoomControl: this.displayZoom,
        streetViewControl: false,
        mapTypeControl: false,
        gestureHandling: "greedy",
        fullscreenControl: this.hasFullScreenButton,
    };

    private markersInBounds$ = new ReplaySubject<MarkerData[]>();

    private subscription: Subscription = new Subscription();

    private mapMovement$: ReplaySubject<boolean> = new ReplaySubject();

    private isNewMapEvent$ = new ReplaySubject<boolean>();

    constructor(
        @Inject(LOCALE) private locale: Locale,
        private context: Context,
        private ngZone: NgZone,
        public loadGeoLibrariesService: LoadGeoLibrariesService,
        public externalLibraryFacade: ExternalLibraryFacade,
    ) {
        super();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasFullScreenButton) {
            this.options.fullscreenControl =
                changes.hasFullScreenButton.currentValue;
        }
        if (changes.displayZoom) {
            this.options.zoomControl = changes.displayZoom.currentValue;
        }
    }

    public ngOnInit(): void {
        // isNewMapEvent$ Observable is true of each new emission of mapEvent
        this.subscription.add(
            this.mapEvent$.subscribe(() => {
                this.isNewMapEvent$.next(true);
            }),
        );

        this.subscription.add(
            this.mapMovement$
                .pipe(
                    filter((movement) => !movement),
                    // If we are no longer in movement, the Observable markersInBounds has already emitted a new value
                    withLatestFrom(this.markersInBounds$),
                    map(([, markers]) => markers),
                    debounceTime(100),
                    // Use pairwise operator to also observe previous value of `markers` data.
                    // Because pairwise need of 2 emissions to get the previous and current value,
                    // use of `startWith` to force `pairwise` to use [] as a first emission
                    startWith([]),
                    pairwise(),
                    withLatestFrom(this.isNewMapEvent$),
                    switchMap(([[previous, current], isNewMapEvent]) => {
                        // If the movement is due to a new MapEvent : always send the markers in bounds, even if they have not changed !
                        // Else, the movement is due to a user event (drag / zoom) so we send a new event only if there are new markers in bounds.
                        if (
                            isNewMapEvent ||
                            !this.compareMarkersList(previous, current)
                        ) {
                            return of(current);
                        }
                        // Ignore this emission and send EMPTY .
                        return EMPTY;
                    }),
                    tap(() => {
                        // MapEvent has been processed once : the next movements will not be due to a new MapEvent.
                        this.isNewMapEvent$.next(false);
                    }),
                )
                .subscribe((markers) => {
                    this.markersCoordinatesInBounds.emit(markers);
                }),
        );

        this.mapManagement();
        this.clickInZoomManagement();
        if (this.isMarkerClustererActive) {
            this.loadMarkerClusterer();
        }
    }

    public ngAfterViewInit(): void {
        // In some case (SPA navigation for exemple) the GoogleMaps element already exists and `googleMapElement.changes` is not triggered.
        // In this case, no need to listen for changes. We can directly set the `googleMapComponent` attribut.
        if (this.googleMapElement.length === 1) {
            this.initializeGoogleMapComponent(this.googleMapElement.first);
            return;
        }
        this.subscription.add(
            this.googleMapElement.changes
                .pipe(
                    filter(
                        (elements: QueryList<GoogleMap>) => elements.length > 0,
                    ),
                    take(1),
                )
                .subscribe((element) => {
                    this.initializeGoogleMapComponent(element.first);
                }),
        );
    }

    public ngOnDestroy(): void {
        if (
            this.context.isInBrowserMode() &&
            this.googleMapComponent?.googleMap
        ) {
            google.maps.event.clearListeners(
                // eslint-disable-next-line @typescript-eslint/ban-types
                this.googleMapComponent.googleMap,
                "idle",
            );
            google.maps.event.clearListeners(
                // eslint-disable-next-line @typescript-eslint/ban-types
                this.googleMapComponent.googleMap,
                "bounds_changed",
            );
        }

        this.subscription.unsubscribe();
    }

    /**
     * emit markersCoordinatesInBounds event with store list in the map bounds
     *
     * @param bounds google bounds of map frame
     */
    public boundsChanged(): void {
        if (this.markers.length > 0) {
            const bounds: google.maps.LatLngBounds | null | undefined =
                this.googleMapComponent?.getBounds();

            // emit a new list of markers in bounds ony if the markers list has changed.
            this.markersInBounds$.next(
                this.getMarkersCoordinatesInBounds(bounds),
            );
        }
    }

    public mapManagement(): void {
        this.subscription.add(
            this.mapInitialized
                .pipe(
                    filter((status) => status),
                    switchMap(() => this.mapEvent$),
                )
                .subscribe((mapEvent) => {
                    switch (mapEvent.mode) {
                        case "default": {
                            this.defaultMode(mapEvent.center);
                            break;
                        }
                        case "at-least-one-marker": {
                            this.atLeastOneMarkerMode(
                                mapEvent.center,
                                mapEvent.bounds,
                            );
                            break;
                        }
                        case "specific-points": {
                            if (mapEvent.points.length === 1) {
                                this.defaultMode(mapEvent.points[0]);
                                break;
                            }
                            this.specificMarkersMode(mapEvent.points);
                            break;
                        }
                        case "all-markers": {
                            this.allMarkersMode();
                            break;
                        }
                    }
                }),
        );
    }

    public atLeastOneMarkerMode(center: Coordinates, bounds?: MapBounds): void {
        const googleMapBounds = new google.maps.LatLngBounds();

        if (bounds) {
            googleMapBounds.union({
                west: bounds.west,
                east: bounds.east,
                south: bounds.south,
                north: bounds.north,
            });
        } else {
            this.extendBounds(center, googleMapBounds);
        }

        // If there are no visible markers in the bounds then add the one closest to the center
        if (this.getMarkersCoordinatesInBounds(googleMapBounds).length === 0) {
            const closestMarker = this.getClosestMarker(this.markers, center);
            this.extendBounds(closestMarker.coordinates, googleMapBounds);
        }

        this.googleMapComponent?.fitBounds(
            googleMapBounds,
            this.hasFullScreenButton ? 10 : 110,
        );

        // fix Google Maps rendering issue by forcing a refresh after the map has finished moving
        // e.g., switching between Kansai region and Tokyo city.
        if (this.googleMapComponent?.googleMap) {
            google.maps.event.addListenerOnce(
                this.googleMapComponent.googleMap,
                "idle",
                () => {
                    this.googleMapComponent?.googleMap?.setCenter(
                        googleMapBounds.getCenter(),
                    );
                },
            );
        }
    }

    public specificMarkersMode(coordinates: Coordinates[]): void {
        const bounds = new google.maps.LatLngBounds();
        coordinates.forEach((oneCoordinates) => {
            this.extendBounds(oneCoordinates, bounds);
        });
        this.googleMapComponent?.fitBounds(bounds);
    }

    public allMarkersMode(): void {
        const bounds = new google.maps.LatLngBounds();
        this.markers.forEach((marker) => {
            this.extendBounds(marker.coordinates, bounds);
        });
        this.googleMapComponent?.fitBounds(bounds);
    }

    public getMarkersCoordinatesInBounds(
        bounds: google.maps.LatLngBounds | null | undefined,
    ): MarkerData[] {
        if (bounds) {
            return this.markers.filter((marker) =>
                bounds.contains({
                    lat: marker.coordinates.latitude,
                    lng: marker.coordinates.longitude,
                }),
            );
        }
        return [];
    }

    public extendBounds(
        coordinates: Coordinates,
        bounds: google.maps.LatLngBounds,
    ): void {
        bounds.extend({
            lat: coordinates.latitude,
            lng: coordinates.longitude,
        });
    }

    public getClosestMarker(
        markers: MarkerData[],
        center: Coordinates,
    ): MarkerData {
        return [...markers].sort(
            (markerA, markerB) =>
                calculateDistance(markerA.coordinates, center) -
                calculateDistance(markerB.coordinates, center),
        )[0];
    }

    public compareMarkersList(
        markersA: MarkerData[],
        markersB: MarkerData[],
    ): boolean {
        return (
            markersA.length === markersB.length &&
            markersA.every((marker, index) => marker === markersB[index])
        );
    }

    private clickInZoomManagement() {
        this.subscription.add(
            this.clickedMarkerCoordinates
                .pipe(filter(() => this.isZoomOnClickActive))
                .subscribe((coordinates: Coordinates) => {
                    const zoom = this.googleMapComponent?.getZoom();
                    this.defaultMode(coordinates);
                    if (this.googleMapComponent?.googleMap && zoom) {
                        if (zoom < DEFAULT_ZOOM_LEVEL) {
                            this.googleMapComponent?.googleMap?.setZoom(
                                DEFAULT_ZOOM_LEVEL,
                            );
                            return;
                        }
                        // If the zoom is already high (and to avoid a dezoom effect),
                        // the zoom is slowly increased
                        this.googleMapComponent?.googleMap?.setZoom(
                            zoom + DEFAULT_INCREMENT_ZOOM_LEVEL,
                        );
                    }
                }),
        );
    }

    private defaultMode(coordinates: Coordinates): void {
        if (this.googleMapComponent?.googleMap) {
            this.googleMapComponent?.googleMap.setCenter({
                lat: coordinates.latitude,
                lng: coordinates.longitude,
            });
            this.googleMapComponent?.googleMap.setZoom(DEFAULT_ZOOM_LEVEL);
        }
    }

    private initializeGoogleMapComponent(googleMap: GoogleMap): void {
        this.googleMapComponent = googleMap;

        this.googleMapComponent.googleMap?.addListener("idle", () => {
            this.ngZone.run(() => {
                this.mapMovement$.next(false);
            });
        });

        this.googleMapComponent.googleMap?.addListener("bounds_changed", () => {
            this.ngZone.run(() => {
                this.mapMovement$.next(true);
            });
        });

        this.mapInitialized.emit(true);
    }

    /**
     * /!\ When in CSR side, Google must be loaded before the Marker Clusterer.
     *
     * On the server side, we "just" want to add the `script` in the head of the page.
     */
    private loadMarkerClusterer(): void {
        const isGoogleLoaded$ = this.context.isInServerMode()
            ? of(true)
            : this.loadGeoLibrariesService.isGoogleLoaded$;

        this.subscription.add(
            isGoogleLoaded$
                .pipe(filter((isLoaded) => isLoaded))
                .subscribe(() => {
                    this.externalLibraryFacade.load({
                        key: "MARKERCLUSTERER",
                        url: `${this.locale.urlPrefix}/assets/js/markerclustererplus/index.min.js`,
                        windowAttribute: "MarkerClusterer",
                    });
                }),
        );
    }
}
